// functionMutation.js
// Draft-write helpers for the Function builder surfaces.
//
// A Function is edited as a mutable DRAFT (the head document at
// `users/{company}/data_functions/{function_id}` plus its `blocks`
// subcollection). Drafts never execute: only immutable published versions at
// `data_functions/{function_id}/versions/{n}` do. Publishing is a backend
// operation (`validate_data_function` with `publish: true`) and is the only
// path that creates a version or resets a Trigger's Email permission.
//
// Because a draft edit cannot cause anything to run, an edit needs no safety
// side effect — but it does need the advisory edit lease (H52): a builder
// session displaced by a takeover must not overwrite the new editor's work,
// so every write here runs as a transaction that first verifies
// `edit_lease.session_id` on the head document and refuses the write with
// `EditLeaseLostError` otherwise. A refused or failed transaction changes
// nothing.
//
// Chart display settings are not a draft edit at all: they change how a chart
// is drawn, not what a function does, so ChartSettings writes them directly
// (in its own transaction) and does not set the dirty flag.

import { assertResourceAvailable } from './resourceLifecycle';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';

// The head no longer names this session in `edit_lease`: another session took
// over (or the function was deleted). The builder ends its editing session.
export class EditLeaseLostError extends Error {
  constructor() {
    super('This editing session no longer holds the edit lock.');
    this.code = 'edit_lease_lost';
  }
}

export const isEditLeaseLost = (error) => error?.code === 'edit_lease_lost';

const assertLeaseHeld = (snapshot, sessionId) => {
  if (!snapshot.exists()) throw new EditLeaseLostError();
  assertResourceAvailable(snapshot, 'Function');
  const lease = snapshot.data()?.edit_lease;
  if (!lease || lease.session_id !== sessionId) throw new EditLeaseLostError();
};

// Commit one draft mutation: the caller's head/block writes together with the
// advisory dirty flag, refused unless `sessionId` still holds the edit lease.
//
// Params:
//   database    - the Firestore instance
//   companyId   - tenant path segment
//   functionId  - function path segment
//   writes      - { functionUpdate, blockWrites } where `functionUpdate` is a
//                 map of head-document fields (nodes, edges, name, ...) and
//                 `blockWrites` is a list of
//                 [{ blockId, op: 'set' | 'update' | 'delete', data }].
//   sessionId   - the builder session's lease identity.
export async function commitDraftMutation(database, companyId, functionId, writes = {}, sessionId = null) {
  const { functionUpdate = {}, blockWrites = [] } = writes;
  const functionRef = doc(database, `users/${companyId}/data_functions/${functionId}`);

  await runTransaction(database, async (transaction) => {
    assertLeaseHeld(await transaction.get(functionRef), sessionId);
    transaction.update(functionRef, {
      ...functionUpdate,
      has_unpublished_changes: true,
      updated_at: serverTimestamp(),
    });
    blockWrites.forEach((write) => {
      const blockRef = doc(database, `users/${companyId}/data_functions/${functionId}/blocks/${write.blockId}`);
      if (write.op === 'delete') {
        transaction.delete(blockRef);
      } else if (write.op === 'set') {
        transaction.set(blockRef, write.data);
      } else {
        transaction.update(blockRef, write.data);
      }
    });
  });
}

// Display-only head writes (canvas geometry). No dirty flag — geometry is
// stripped from published content — but still lease-checked, because `nodes`
// and `edges` ARE the executable graph a displaced session must not clobber.
export async function commitDisplayMutation(database, companyId, functionId, fields = {}, sessionId = null) {
  const functionRef = doc(database, `users/${companyId}/data_functions/${functionId}`);
  await runTransaction(database, async (transaction) => {
    assertLeaseHeld(await transaction.get(functionRef), sessionId);
    transaction.update(functionRef, { ...fields });
  });
}
